import * as React from 'react';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Button from '@mui/material/Button';
import { IProjectDefaultFormValues, IProjectFormValues, projectFormSchema } from '../../../../@types/model/masterData/project/projectFormValues';
import { useAppSelector } from '../../../../@types/redux';
import { PillButton, ControlledTextField, ControlledMuiAutoCompleteSelect } from '@zz2/zz2-ui';
import { ControlledLabelledCheckBox } from '../../../../components/input/form/ControlledLabelledCheckBoxWrapper';
import { IOptionType } from '../../../../@types/helper';
import CustomTooltip from '../../../../components/tooltip/tooltip';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import ControlledDateInput from '../../../../components/input/form/ControlledDateInput';
import CustomReactHookFormDevTools from '../../../../components/devtools/ReactHookFormDevTools';

interface IProjectFormProps {
    isLoading : boolean;
    initialValues : IProjectFormValues | IProjectDefaultFormValues;
    onSubmit : (values : IProjectFormValues) => void;
    onClose : () => void;
}

const ProjectForm = (props : IProjectFormProps) : React.ReactElement => {
    const schema = projectFormSchema;
    const { initialValues, onSubmit, onClose, isLoading } = props;

    const { handleSubmit, control, formState: { isValid, isDirty } } = useForm<IProjectFormValues>({
        defaultValues: initialValues,
        resolver: zodResolver(schema),
        mode: 'onChange',
    });

    const isImported = initialValues.isImported ?? false;

    const commodities = useAppSelector(state => state.masterData.commodities);

    const commodityOptions : Array<IOptionType> = useMemo(() => {
        return commodities?.filter(x => x.isActive).map(x => ({ label: `(${x.code}) ${x.name}`, value: x.id })) ?? [];
    }, [commodities]);

    const tooltipTitle = isImported ? 'Cannot change this field if project is imported' : '';

    return (
        <div>
            <CustomReactHookFormDevTools control={control} />
            <form onSubmit={handleSubmit(onSubmit)} className={'p20'}>
                <div className={'fdc aic'}>
                    <CustomTooltip title={tooltipTitle} className={'wfill'}>
                        <div className={'mb10'}>
                            <ControlledTextField
                                className={'p0 wfill'}
                                name={'number'}
                                label={'Number'}
                                control={control}
                                defaultValue={initialValues.number}
                                disabled={isImported} />
                        </div>
                    </CustomTooltip>
                    <CustomTooltip title={tooltipTitle} className={'wfill'}>
                        <div className={'mb10'}>
                            <ControlledTextField
                                className={'p0 wfill'}
                                name={'description'}
                                label={'Description'}
                                control={control}
                                defaultValue={initialValues.description}
                                disabled={isImported} />
                        </div>
                    </CustomTooltip>
                    <CustomTooltip title={tooltipTitle} className={'wfill'}>
                        <ControlledMuiAutoCompleteSelect
                            className={'p0 wfill'}
                            name={'commodity'}
                            label={'Commodity'}
                            control={control}
                            options={commodityOptions}
                            defaultValues={initialValues.commodity ?? null}
                            disabled={isImported} />
                    </CustomTooltip>
                    <CustomTooltip title={tooltipTitle}>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <ControlledDateInput
                                name={'endedOn'}
                                label={'Ended On'}
                                control={control}
                                defaultValue={initialValues.endedOn ?? undefined}
                                className={'p0 mt10'}
                                disabled={isImported}
                                type={'date-fns'}/>
                        </LocalizationProvider>
                    </CustomTooltip>
                    <CustomTooltip title={tooltipTitle}>
                        <div className={'fdr jcfs mb10'}>
                            <ControlledLabelledCheckBox
                                className={'p0'}
                                name={'isActive'}
                                label={'Is Active'}
                                control={control}
                                defaultValue={initialValues.isActive}
                                disabled={isImported} />
                        </div>
                    </CustomTooltip>
                    <div className={'fdr ml10 ais jcfe pt10 pb10'}>
                        <Button className={'cpd mr20'} type={'button'} onClick={onClose}>
                            CANCEL
                        </Button>
                        <PillButton
                            disabled={isImported ? !isValid || isLoading : (!isDirty || !isValid || isLoading)}
                            className={'ml15 pl20 pr20 h35'}
                            text={'Save'}
                            type={'submit'}
                            color={'secondary'} />
                    </div>
                </div>
            </form>
        </div>
    );
};

export default ProjectForm;
